import { useEffect, useState } from "react";
import {
  ApiError,
  fetchIndexSimulation,
  fetchInstrumentDetail,
  fetchMarketIndices,
  fetchMarketMomentum,
  fetchMarketOverview,
  type IndexCardDto,
  type IndexSimResultDto,
  type InstrumentDetailDto,
  type MarketOverviewDto,
  type MomentumDto,
} from "../api";
import { PriceChart } from "../components/PriceChart";
import { FlowsChart } from "../components/FlowsChart";
import { FlowsRelativeChart } from "../components/FlowsRelativeChart";
import { FlowsRollingCorrChart } from "../components/FlowsRollingCorrChart";
import { FlowsCumulativeChart } from "../components/FlowsCumulativeChart";
import { FlowsNetChart } from "../components/FlowsNetChart";
import { ValuationBandChart } from "../components/ValuationBandChart";
import { CapeChart } from "../components/CapeChart";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { AsOfBadge } from "../components/AsOfBadge";
import { MarketNewsPanel } from "../components/MarketNewsPanel";

type FlowsTab = "daily" | "relative" | "rolling" | "cumulative" | "net";

const FLOWS_TABS: { id: FlowsTab; label: string }[] = [
  { id: "daily", label: "Daily flows" },
  { id: "relative", label: "Relative moves" },
  { id: "rolling", label: "Rolling correlation" },
  { id: "cumulative", label: "Cumulative flows" },
  { id: "net", label: "FII vs DII" },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtPct(v: number | null, digits = 2): string {
  return v !== null ? `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%` : "—";
}

function fmtCorr(v: number | null): string {
  return v !== null ? `${v >= 0 ? "+" : ""}${v.toFixed(2)}` : "—";
}

const REGIME_LABEL: Record<IndexCardDto["sigma_regime_status"], string> = {
  ADEQUATE: "adequate",
  DEGRADED: "degraded",
  REGIME_BREAK: "regime break",
  NO_DATA: "no data yet",
};

function RegimeBadge({ status }: { status: IndexCardDto["sigma_regime_status"] }) {
  return <span className={`regime-badge ${status.toLowerCase()}`}>{REGIME_LABEL[status]}</span>;
}

/** Domestic vs global at a glance on the Macro & Events panel -- "IN"/"US"
 * parsed server-side from the event_type's own prefix
 * (app.ingest.macro_events.country_of), never inferred client-side. */
function CountryBadge({ country }: { country: string | null }) {
  if (country === "IN") return <span className="ev-country ev-country-in" title="India">🇮🇳 IN</span>;
  if (country === "US") return <span className="ev-country ev-country-us" title="United States">🇺🇸 US</span>;
  return null;
}

/** A compact, honest glance-trend line -- real net-advances-declines over
 * BREADTH_HISTORY_DAYS (app/api/routes/market.py), never a full interactive
 * chart with its own axis/zoom (that's react-financial-charts' territory,
 * used everywhere this page shows a real price/MC series). This is the same
 * "small inline visual built from real data" tier as the existing adbar and
 * Sectors-Today tiles -- a plain SVG polyline, no library, no fabricated
 * points: every vertex is one of the real values passed in. */
//: Internal coordinate space for the sparkline/fan SVGs below --
//: deliberately just a computation grid, NOT the rendered pixel size.
//: Both stretch to their actual container via CSS (width:100%) +
//: `preserveAspectRatio="none"`, so this only needs enough resolution for
//: the path math to look smooth; the real on-screen size is CSS's job.
const SPARK_VBOX_W = 200;
const SPARK_VBOX_H = 44;

function BreadthSparkline({ points }: { points: { date: string; net_adv_decl: number }[] }) {
  if (points.length < 5) {
    return <span className="note">insufficient history ({points.length}d)</span>;
  }
  const vals = points.map((p) => p.net_adv_decl);
  const lo = Math.min(...vals, 0);
  const hi = Math.max(...vals, 0);
  const span = hi - lo || 1;
  const x = (i: number) => (i / (points.length - 1)) * SPARK_VBOX_W;
  const y = (v: number) => SPARK_VBOX_H - ((v - lo) / span) * SPARK_VBOX_H;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.net_adv_decl).toFixed(1)}`).join(" ");
  const zeroY = y(0).toFixed(1);
  const last = vals[vals.length - 1];
  return (
    <svg viewBox={`0 0 ${SPARK_VBOX_W} ${SPARK_VBOX_H}`} preserveAspectRatio="none" className="breadth-spark">
      <line x1={0} y1={zeroY} x2={SPARK_VBOX_W} y2={zeroY} className="breadth-spark-zero" />
      <path d={path} className={`breadth-spark-line ${last >= 0 ? "pos" : "neg"}`} fill="none" />
    </svg>
  );
}

//: India VIX condition-read thresholds -- descriptive labels only, no
//: trading signal implied. Conventional VIX-style bands (a genuine "fear
//: gauge" reading, not a forecast of anything).
function vixQualifier(vix: number): string {
  if (vix < 13) return "calm";
  if (vix < 20) return "normal";
  if (vix < 30) return "elevated";
  return "high fear";
}

function HealthBanner({ sources, asOf }: { sources: MarketOverviewDto["source_health"]; asOf: string }) {
  if (sources.length === 0) {
    return (
      <div className="health-banner">
        <span className="health-dot no_data" />
        <span>
          No source-health data recorded. Showing data as of <b>{asOf}</b>.
        </span>
      </div>
    );
  }
  const freshCount = sources.filter((s) => s.status === "FRESH").length;
  const allFresh = freshCount === sources.length;
  return (
    <div className="health-banner">
      <span className={`health-dot ${allFresh ? "adequate" : "degraded"}`} />
      <span>
        <b>{freshCount} of {sources.length} sources fresh</b> · data as of {asOf}
      </span>
      <span className="health-detail">
        {sources
          .filter((s) => s.status !== "FRESH")
          .map((s) => `${s.source}: ${s.status.toLowerCase()}`)
          .join(" · ") || "all sources fresh"}
      </span>
    </div>
  );
}

function fmtVolFraction(v: number | null): string {
  // realized_vol / predicted_sigma are stored as fractions (0.123 = 12.3%).
  return v !== null ? `${(v * 100).toFixed(1)}%` : "—";
}

function fmtImpliedVol(v: number | null): string {
  // India VIX is already a real index level/percentage number (12.16 means
  // 12.16%) -- x100 here would be a real, silent unit-blending bug, exactly
  // what A6's "never blended" rule warns against.
  return v !== null ? `${v.toFixed(1)}%` : "—";
}

/** A benchmark card renders REAL close/change and ui_spec A6's three vol
 * lenses -- realized, forecast, implied -- labelled and never blended (A6's
 * own words). It deliberately has NO ring, arrow, or "confidence% up/down"
 * -- that would be a directional signal, and the index track resolved mu to
 * NEUTRAL DRIFT (0/42 blessed features cleared significance across 4
 * pilots). `no_directional_signal_note` carries the real reason and is
 * always shown, not hidden in a tooltip -- this is the one surface where
 * the index resolution becomes user-facing, so the caveat gets the same
 * visual weight as the number.
 *
 * Implied vol (VIX) is NIFTY-only per A6's own qualifier -- `card.
 * implied_vol` is null for every other index, never a fabricated proxy.
 * When present, it fills the trio's third cell and the sigma-regime badge
 * moves below the trio (a lens is mandated for that slot; regime is not).
 * When absent (SENSEX), sigma-regime fills the third cell instead -- the
 * trio would otherwise show something outside A6's three-lens vocabulary,
 * per the reconciliation this fixes: RSI(14) never belonged here at all. */
const FORWARD_HORIZONS = [5, 10, 30];

/** Fetches the index MC range for one (securityId, horizon) pair. Shared by
 * every "forward view" rendering on this page (the Benchmarks section's
 * shared-horizon dual fan, the single-index drill-in view) so there is one
 * fetch/cancel implementation, not several copies that could drift. */
function useIndexSimulation(token: string, securityId: string, horizon: number) {
  const [sim, setSim] = useState<IndexSimResultDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setErr(null);
    fetchIndexSimulation(token, securityId, horizon)
      .then((r) => {
        if (!cancelled) setSim(r);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : "request failed");
      });
    return () => {
      cancelled = true;
    };
  }, [token, securityId, horizon]);
  return { sim, err };
}

function HorizonTabs({ horizon, onChange }: { horizon: number; onChange: (h: number) => void }) {
  return (
    <div className="fwd-horizon-tabs">
      {FORWARD_HORIZONS.map((h) => (
        <button
          key={h}
          type="button"
          className={`fwd-h-btn ${horizon === h ? "active" : ""}`}
          onClick={() => onChange(h)}
        >
          {h}d
        </button>
      ))}
    </div>
  );
}

/** The cone-widens-over-the-horizon visual the mockup specified, built
 * directly from the MC engine's own real percentile bands (simulate_gbm's
 * path percentiles -- see simulation_engine.py) -- not a decorative shape.
 * Same "small inline SVG built from real data" tier as BreadthSparkline
 * above: a glance-level visual with no axis/zoom/tooltip, not a
 * react-financial-charts panel, since it carries no interactivity beyond
 * what the text readout below it already states in numbers. */
function MiniFanChart({ bands }: { bands: IndexSimResultDto["bands"] }) {
  const p10 = bands.percentiles["10"];
  const p25 = bands.percentiles["25"];
  const p75 = bands.percentiles["75"];
  const p90 = bands.percentiles["90"];
  const p50 = bands.percentiles["50"];
  if (!p10 || !p90 || !p50 || p10.length < 2) return null;

  const n = p10.length;
  const all = [...p10, ...p90];
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const span = hi - lo || 1;
  const x = (i: number) => (i / (n - 1)) * SPARK_VBOX_W;
  const y = (v: number) => SPARK_VBOX_H - ((v - lo) / span) * SPARK_VBOX_H;

  const band = (inner: number[], outer: number[]) => {
    const fwd = inner.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    const bwd = outer.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).reverse();
    return [...fwd, ...bwd].join(" ");
  };
  const medianPath = p50.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${SPARK_VBOX_W} ${SPARK_VBOX_H}`} preserveAspectRatio="none" className="fan-mini">
      <polygon points={band(p10, p90)} className="fan-mini-outer" />
      {p25 && p75 && <polygon points={band(p25, p75)} className="fan-mini-inner" />}
      <path d={medianPath} className="fan-mini-median" fill="none" />
    </svg>
  );
}

/** The honest "how will it behave over N days" read (see
 * app/factory/simulation_service.py's "INDEX MC" section for the full
 * evidence): index direction has no measured forecastable edge at h=5/10/30
 * (index_direction_pilot.py, extending CLAUDE.md's existing h=5/h=70
 * neutral-drift finding), so this is deliberately a RANGE from real
 * persistence volatility, centered on no directional call, never a
 * disguised directional forecast wearing a chart. Just the fan + caveats --
 * the horizon TAB CONTROL is rendered once by the caller (shared across
 * both benchmarks in the Benchmarks section; its own header when used
 * standalone in the Momentum drill-in). The "range, not direction" honesty
 * note is a SHORT visible label + hover (FieldHelp) here -- kept short
 * deliberately since this component renders up to 3 times per page (both
 * benchmarks' fans + the Momentum drill-in), and a full paragraph that many
 * times drowns the page rather than informing it; the full explanation
 * still lives on hover, not deleted. */
function IndexForwardFan({ token, securityId, horizon }: { token: string; securityId: string; horizon: number }) {
  const { sim, err } = useIndexSimulation(token, securityId, horizon);
  const warnings = sim?.caveats.filter((c) => c.severity === "warning") ?? [];

  if (err) return <div className="note">{err}</div>;
  if (!sim || sim.horizon_days !== horizon) {
    return <div className="empty-state" style={{ padding: 8 }}>Loading…</div>;
  }
  return (
    <>
      <MiniFanChart bands={sim.bands} />
      <div className="fwd-range">
        <span className={sim.terminal_summary.p10_return_pct >= 0 ? "pos" : "neg"}>
          {fmtPct(sim.terminal_summary.p10_return_pct)}
        </span>
        <span className="fwd-range-sep">to</span>
        <span className={sim.terminal_summary.p90_return_pct >= 0 ? "pos" : "neg"}>
          {fmtPct(sim.terminal_summary.p90_return_pct)}
        </span>
        <span className="note"> 10–90% band · vol {fmtVolFraction(sim.inputs.vol.sigma_annual)} ann.</span>
      </div>
      <div className="hint-line">
        range only · direction not forecastable <FieldHelp term="index_mc_range" />
        <AsOfBadge date={sim.inputs.vol.as_of_date} label="vol input" />
      </div>
      {warnings.map((c, i) => (
        <div className="no-signal-note" key={i}>
          ⚠ {c.message}
        </div>
      ))}
    </>
  );
}

/** Single-index forward view, its own horizon tabs included -- used by the
 * Momentum section's drill-in, where only one index is shown at a time. */
function IndexForwardView({ token, securityId }: { token: string; securityId: string }) {
  const [horizon, setHorizon] = useState(5);
  return (
    <div className="fwd-view">
      <div className="fwd-view-head">
        <span className="tk2">
          Forward view ({horizon}d) <FieldHelp term="index_mc_range" />
        </span>
        <HorizonTabs horizon={horizon} onChange={setHorizon} />
      </div>
      <IndexForwardFan token={token} securityId={securityId} horizon={horizon} />
    </div>
  );
}

/** ui_spec A6's three vol lenses for one benchmark -- realized, forecast,
 * implied (NIFTY-only) or sigma-regime in implied's place -- labelled and
 * never blended. Deliberately does NOT render price/change or the forward
 * view; the Benchmarks section renders those once each (chart header, and
 * the shared dual forward-view column) rather than per-card. */
function BenchmarkVolTrio({ card, expectedAsOf }: { card: IndexCardDto; expectedAsOf: string }) {
  const hasImplied = card.implied_vol !== null;
  return (
    <div className="bench-voltrio-card">
      <div className="bench-voltrio-name">
        {card.display_ticker ?? card.security_id}
        <AsOfBadge date={card.bar_date} reference={expectedAsOf} />
      </div>
      <div className="bench-trio">
        <div className="tcell">
          <span className="tk2">Realized vol (60d) <FieldHelp term="realized_vol" /></span>
          <span className="tv2">{fmtVolFraction(card.realized_vol)}</span>
        </div>
        <div className="tcell">
          <span className="tk2">Forecast vol <FieldHelp term="real_magnitude_sigma" /></span>
          <span className="tv2">{fmtVolFraction(card.predicted_sigma)}</span>
        </div>
        {hasImplied ? (
          <div className="tcell">
            <span className="tk2">Implied vol (VIX) <FieldHelp term="implied_vol" /></span>
            <span className="tv2">{fmtImpliedVol(card.implied_vol)}</span>
          </div>
        ) : (
          <div className="tcell">
            <span className="tk2">σ regime</span>
            <span className="tv2">
              <RegimeBadge status={card.sigma_regime_status} />
            </span>
            <AsOfBadge date={card.sigma_regime_as_of} reference={expectedAsOf} />
          </div>
        )}
      </div>
      {hasImplied && (
        <div className="bench-regime-row">
          <span className="tk2">σ regime</span>
          <RegimeBadge status={card.sigma_regime_status} />
          <AsOfBadge date={card.sigma_regime_as_of} reference={expectedAsOf} />
        </div>
      )}
      <div className="hint-line" title={card.no_directional_signal_note}>
        ⚠ no directional signal · measured, not an oversight <FieldHelp term="no_directional_signal" />
      </div>
      {card.sigma_regime_note && <div className="no-signal-note">{card.sigma_regime_note}</div>}
    </div>
  );
}

/** BENCHMARKS section: a toggled candlestick chart (2/3) alongside a
 * shared-horizon dual forward view for BOTH NIFTY and SENSEX at once (1/3),
 * with both benchmarks' vol-trio cards along the bottom -- one section
 * replacing the old two-card side-by-side layout, so the forward view and
 * the vol lenses are compared directly rather than duplicated per card. */
function BenchmarksSection({
  token, benchmarks, detailByIndex, expectedAsOf,
}: {
  token: string; benchmarks: IndexCardDto[]; detailByIndex: Record<string, InstrumentDetailDto | null>;
  expectedAsOf: string;
}) {
  const [toggle, setToggle] = useState(benchmarks[0]?.security_id ?? "IDX:NIFTY");
  const [horizon, setHorizon] = useState(5);
  const chosen = benchmarks.find((b) => b.security_id === toggle) ?? null;
  const detail = detailByIndex[toggle] ?? null;

  return (
    <div className="detail-card">
      <div className="detail-card-head">
        <span>Benchmarks</span>
      </div>
      <div className="bench-layout">
        <div className="bench-chart-col">
          <div className="bench-toggle" role="tablist" aria-label="Benchmark chart">
            {benchmarks.map((b) => (
              <button
                key={b.security_id}
                type="button"
                className={`bench-toggle-btn ${toggle === b.security_id ? "active" : ""}`}
                onClick={() => setToggle(b.security_id)}
              >
                {b.display_ticker ?? b.security_id}
              </button>
            ))}
          </div>
          {chosen && (
            <div className="bench-chart-header">
              <span className="bench-level">
                {chosen.close !== null ? chosen.close.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}
              </span>
              <span className={`bench-chg ${chosen.day_change_pct === null ? "mut" : chosen.day_change_pct >= 0 ? "pos" : "neg"}`}>
                {fmtPct(chosen.day_change_pct)}
              </span>
              <AsOfBadge date={chosen.bar_date} reference={expectedAsOf} label="price" />
            </div>
          )}
          {!detail ? (
            <div className="empty-state" style={{ padding: 20 }}>Loading…</div>
          ) : (
            <PriceChart
              bars={detail.bars}
              caMarkers={detail.ca_markers}
              earningsMarkers={[]}
              macroMarkers={detail.macro_markers}
              indicators={[]}
            />
          )}
        </div>
        <div className="bench-forward-col">
          <div className="fwd-view-head">
            <span className="tk2">
              Forward view ({horizon}d) <FieldHelp term="index_mc_range" />
            </span>
            <HorizonTabs horizon={horizon} onChange={setHorizon} />
          </div>
          {benchmarks.map((b) => (
            <div className="bench-forward-item" key={b.security_id}>
              <div className="bench-forward-label">{b.display_ticker ?? b.security_id}</div>
              <IndexForwardFan token={token} securityId={b.security_id} horizon={horizon} />
            </div>
          ))}
        </div>
      </div>
      <div className="bench-voltrio-row">
        {benchmarks.map((b) => (
          <BenchmarkVolTrio card={b} expectedAsOf={expectedAsOf} key={b.security_id} />
        ))}
      </div>
    </div>
  );
}

/** STRONGEST MOMENTUM: ranks the 17 indices (never stocks -- see
 * app/api/routes/market.py's own comment) by real price return over 5
 * timeframes. Descriptive, backward-looking -- "who moved most", not a
 * forecast. Drill-in reuses the exact same candle chart + index forward
 * view as the Benchmarks section, so the honest range means the same thing
 * everywhere on this page. */
/** The compact list-left (~260px) + detail-right layout from the approved
 * mockup (market_section_mockup.html's ".mom-layout" / ".mom-detail") --
 * one row, not a full-width list stacked above a full-width chart. A
 * selection always exists (defaults to rank #1 whenever the timeframe
 * changes or the current selection drops out of the new top 5) so the
 * detail pane is never empty next to a populated list, matching the
 * mockup's "row 1 highlighted by default" state. */
function MomentumSection({ token, asOf, momentum }: { token: string; asOf: string; momentum: MomentumDto | null }) {
  const [tfIdx, setTfIdx] = useState(0);
  const [manualSelection, setManualSelection] = useState<string | null>(null);
  const [drillDetail, setDrillDetail] = useState<InstrumentDetailDto | null>(null);

  const tf = momentum?.timeframes[tfIdx];
  const top5 = tf ? tf.ranked.filter((r) => r.return_pct !== null).slice(0, 5) : [];
  const selectedId = (manualSelection && top5.some((r) => r.security_id === manualSelection))
    ? manualSelection
    : top5[0]?.security_id ?? null;
  const selectedRow = top5.find((r) => r.security_id === selectedId) ?? null;

  useEffect(() => {
    if (!selectedId) {
      setDrillDetail(null);
      return;
    }
    let cancelled = false;
    setDrillDetail(null);
    fetchInstrumentDetail(token, selectedId, asOf)
      .then((d) => {
        if (!cancelled) setDrillDetail(d);
      })
      .catch(() => {
        if (!cancelled) setDrillDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, selectedId, asOf]);

  if (!momentum) {
    return (
      <div className="detail-card">
        <div className="detail-card-head"><span>Strongest momentum</span></div>
        <div className="empty-state" style={{ padding: 20 }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="detail-card">
      <div className="detail-card-head">
        <span>
          Strongest momentum <span className="note">top 5 of 17 indices by real price return — descriptive, not a forecast</span>
        </span>
        <AsOfBadge date={momentum.as_of} />
      </div>
      <div className="flows-tabs" role="tablist" aria-label="Momentum timeframe">
        {momentum.timeframes.map((t, i) => (
          <button
            key={t.label}
            role="tab"
            aria-selected={tfIdx === i}
            className={`flows-tab ${tfIdx === i ? "active" : ""}`}
            onClick={() => {
              setTfIdx(i);
              setManualSelection(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {top5.length === 0 ? (
        <div className="empty-state" style={{ padding: 12 }}>No return data for this timeframe yet.</div>
      ) : (
        <div className="mom-layout">
          <div className="mom-rank-list">
            {top5.map((r, i) => (
              <button
                key={r.security_id}
                type="button"
                className={`mom-rank-row ${selectedId === r.security_id ? "active" : ""}`}
                onClick={() => setManualSelection(r.security_id)}
              >
                <span className="mom-rank-left">
                  <span className="mom-rank-num">{i + 1}</span>
                  <span className="mom-rank-name">{r.display_ticker ?? r.security_id}</span>
                </span>
                <span className={`mom-rank-ret ${(r.return_pct ?? 0) >= 0 ? "pos" : "neg"}`}>{fmtPct(r.return_pct)}</span>
              </button>
            ))}
          </div>
          <div className="mom-detail">
            <div className="mom-detail-head">
              <span className="mom-detail-name">{selectedRow?.display_ticker ?? selectedId}</span>
              <span className="note">
                {tf?.label} return <b className={(selectedRow?.return_pct ?? 0) >= 0 ? "pos" : "neg"}>{fmtPct(selectedRow?.return_pct ?? null)}</b>
              </span>
            </div>
            <div className="mom-detail-split">
              <div className="mom-detail-chart">
                {!drillDetail ? (
                  <div className="empty-state" style={{ padding: 20 }}>Loading…</div>
                ) : (
                  <PriceChart
                    bars={drillDetail.bars}
                    caMarkers={drillDetail.ca_markers}
                    earningsMarkers={[]}
                    macroMarkers={drillDetail.macro_markers}
                    indicators={[]}
                  />
                )}
              </div>
              {selectedId && <IndexForwardView token={token} securityId={selectedId} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function sectorTileColor(pct: number | null): string {
  if (pct === null) return "";
  const t = Math.max(-1, Math.min(1, pct / 2.5));
  return t >= 0 ? `rgba(14,159,110,${0.08 + t * 0.26})` : `rgba(163,42,42,${0.08 + -t * 0.26})`;
}

function SectorHeat({ indices }: { indices: IndexCardDto[] }) {
  return (
    <div className="heat-grid">
      {indices.map((idx) => (
        <div className="heat-cell" style={{ background: sectorTileColor(idx.day_change_pct) }} key={idx.security_id}>
          <div className="heat-name">{idx.display_ticker ?? idx.security_id}</div>
          <div className={`heat-pct ${idx.day_change_pct !== null && idx.day_change_pct >= 0 ? "pos" : "neg"}`}>
            {fmtPct(idx.day_change_pct, 1)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function MarketPage({
  token,
  onUnauthorized,
  onNavigateToIndices,
}: {
  token: string;
  onUnauthorized: () => void;
  onNavigateToIndices: () => void;
}) {
  const [asOf] = useState(todayIso());
  const [data, setData] = useState<MarketOverviewDto | null>(null);
  const [allIndices, setAllIndices] = useState<IndexCardDto[] | null>(null);
  const [niftyDetail, setNiftyDetail] = useState<InstrumentDetailDto | null>(null);
  const [sensexDetail, setSensexDetail] = useState<InstrumentDetailDto | null>(null);
  const [momentum, setMomentum] = useState<MomentumDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flowsTab, setFlowsTab] = useState<FlowsTab>("daily");

  useEffect(() => {
    function authAware(err: unknown): boolean {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return true;
      }
      return false;
    }
    fetchMarketOverview(token, asOf)
      .then(setData)
      .catch((err) => {
        if (!authAware(err)) setError(err instanceof ApiError ? `${err.status}: ${err.message}` : "request failed");
      });
    fetchMarketIndices(token, asOf)
      .then(setAllIndices)
      .catch((err) => {
        if (!authAware(err)) console.error(err);
      });
    fetchInstrumentDetail(token, "IDX:NIFTY", asOf)
      .then(setNiftyDetail)
      .catch((err) => {
        if (!authAware(err)) console.error(err);
      });
    fetchInstrumentDetail(token, "IDX:SENSEX", asOf)
      .then(setSensexDetail)
      .catch((err) => {
        if (!authAware(err)) console.error(err);
      });
    fetchMarketMomentum(token, asOf)
      .then(setMomentum)
      .catch((err) => {
        if (!authAware(err)) console.error(err);
      });
  }, [token, asOf, onUnauthorized]);

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="empty-state">Loading…</div>;

  const { breadth, flows, flows_series, flows_series_note, flows_correlation, macro_upcoming, macro_recent, valuation, cape, benchmarks, source_health } = data;
  const totalMoves = (breadth.advances ?? 0) + (breadth.declines ?? 0) + (breadth.unchanged ?? 0);
  const advPct = totalMoves ? ((breadth.advances ?? 0) / totalMoves) * 100 : 0;
  const decPct = totalMoves ? ((breadth.declines ?? 0) / totalMoves) * 100 : 0;
  const flatPct = 100 - advPct - decPct;
  const niftyVix = benchmarks.find((b) => b.security_id === "IDX:NIFTY")?.implied_vol ?? null;
  const sectorsUpCount = allIndices?.filter((i) => (i.day_change_pct ?? 0) > 0).length ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Market</h1>
          <div className="page-sub">The tide: conditions now, and where the benchmarks stand — as of {data.as_of}</div>
        </div>
      </div>

      <PageHelper>
        The whole-market tide, not a forecast: real breadth, flows, and benchmark levels as of the latest trading
        day with data captured — {data.as_of}, which may lag today if the day's EOD data hasn't published yet.
        Benchmark volatility (σ) is a real, literal magnitude — but these indices deliberately carry{" "}
        <FieldHelp term="no_directional_signal" />no directional prediction (measured, not an oversight).
      </PageHelper>

      <HealthBanner sources={source_health} asOf={data.as_of} />

      <div className="detail-card">
        <div className="detail-card-head">
          <span>
            Breadth <span className="note">conditions, not a forecast</span>
          </span>
          {breadth.available && <AsOfBadge date={breadth.date} reference={data.expected_as_of} />}
        </div>
        {!breadth.available ? (
          <div className="empty-state" style={{ padding: 20 }}>
            No market-breadth reading captured yet.
          </div>
        ) : (
          <div className="breadth-strip">
            <div className="breadth-strip-adv">
              <span className="breadth-strip-label">Advances / Declines</span>
              <b>
                {breadth.advances} ▲ · {breadth.declines} ▼
                {breadth.unchanged ? ` · ${breadth.unchanged} flat` : ""}
              </b>
              <div className="adbar">
                <div className="adv" style={{ width: `${advPct}%` }} />
                <div className="flat" style={{ width: `${flatPct}%` }} />
                <div className="dec" style={{ width: `${decPct}%` }} />
              </div>
            </div>
            <div className="breadth-strip-mets">
              <span className="breadth-strip-met">
                Above 200-DMA <b>{breadth.pct_above_200dma !== null ? `${breadth.pct_above_200dma.toFixed(0)}%` : "—"}</b>
              </span>
              <span className="breadth-strip-met">
                Above 50-DMA <b>{breadth.pct_above_50dma !== null ? `${breadth.pct_above_50dma.toFixed(0)}%` : "—"}</b>
              </span>
              <span className="breadth-strip-met">
                52w highs <b className="pos">{breadth.new_highs ?? "—"}</b>
              </span>
              <span className="breadth-strip-met">
                52w lows <b className="neg">{breadth.new_lows ?? "—"}</b>
              </span>
            </div>
            <div className="breadth-strip-reads">
              <div className="breadth-read">
                <span className="breadth-read-label">Breadth {data.breadth_history.length}d</span>
                <BreadthSparkline points={data.breadth_history} />
              </div>
              <div className="breadth-read">
                <span className="breadth-read-label">India VIX</span>
                <span className="breadth-read-val">
                  {niftyVix !== null ? `${niftyVix.toFixed(1)} · ${vixQualifier(niftyVix)}` : "—"}
                </span>
              </div>
              <div className="breadth-read">
                <span className="breadth-read-label">Sectors up</span>
                <span className="breadth-read-val">
                  {allIndices ? `${sectorsUpCount} of ${allIndices.length}` : "—"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <BenchmarksSection
        token={token}
        benchmarks={benchmarks}
        detailByIndex={{ "IDX:NIFTY": niftyDetail, "IDX:SENSEX": sensexDetail }}
        expectedAsOf={data.expected_as_of}
      />

      <MomentumSection token={token} asOf={asOf} momentum={momentum} />

      <div className="detail-card">
        <div className="filter-row">
          <span className="detail-card-head" style={{ margin: 0 }}>
            Screen the benchmarks
          </span>
          <button className="btn primary" onClick={onNavigateToIndices}>
            ▷ Open Indices screener
          </button>
        </div>
      </div>

      <div className="detail-card">
        <div className="detail-card-head">
          <span>
            Institutional flows <span className="note">{flows.note}</span>
          </span>
          {flows.available && <AsOfBadge date={flows.date} reference={data.expected_as_of} />}
        </div>
        {!flows.available ? (
          <div className="empty-state" style={{ padding: 20 }}>
            No FII/DII flow captured for {data.as_of}.
          </div>
        ) : (
          <>
            <div className="mgrid">
              <div className="met">
                <span className="k">FII net (latest)</span>
                <span className={`v ${flows.fii_net !== null ? (flows.fii_net >= 0 ? "pos" : "neg") : ""}`}>
                  {flows.fii_net !== null ? `₹${flows.fii_net.toLocaleString("en-IN")} Cr` : "—"}
                </span>
              </div>
              <div className="met">
                <span className="k">DII net (latest)</span>
                <span className={`v ${flows.dii_net !== null ? (flows.dii_net >= 0 ? "pos" : "neg") : ""}`}>
                  {flows.dii_net !== null ? `₹${flows.dii_net.toLocaleString("en-IN")} Cr` : "—"}
                </span>
              </div>
            </div>
            {flows_series.length >= 2 && (
              <>
                <div className="flows-tabs" role="tablist" aria-label="Institutional flows view">
                  {FLOWS_TABS.map((t) => (
                    <button
                      key={t.id}
                      role="tab"
                      aria-selected={flowsTab === t.id}
                      className={`flows-tab ${flowsTab === t.id ? "active" : ""}`}
                      onClick={() => setFlowsTab(t.id)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {flowsTab === "daily" && <FlowsChart points={flows_series} />}
                {flowsTab === "relative" && <FlowsRelativeChart points={flows_series} />}
                {flowsTab === "rolling" && <FlowsRollingCorrChart points={flows_series} />}
                {flowsTab === "cumulative" && <FlowsCumulativeChart points={flows_series} />}
                {flowsTab === "net" && <FlowsNetChart points={flows_series} />}

                <div className="flows-series-note">{flows_series_note}</div>

                {(flowsTab === "daily" || flowsTab === "net") && (
                  <div className="flows-correlation">
                    <span className="flows-correlation-label">
                      When fear moves, who buys? <FieldHelp term="flows_correlation" />
                    </span>
                    {flowsTab === "daily" ? (
                      flows_correlation.corr_vix_fii === null ? (
                        <span className="note">{flows_correlation.note}</span>
                      ) : (
                        <span>
                          VIX↑ vs FII: <b className={flows_correlation.corr_vix_fii >= 0 ? "pos" : "neg"}>
                            {fmtCorr(flows_correlation.corr_vix_fii)}
                          </b>
                          {" · vs DII: "}
                          <b className={(flows_correlation.corr_vix_dii ?? 0) >= 0 ? "pos" : "neg"}>
                            {fmtCorr(flows_correlation.corr_vix_dii)}
                          </b>
                          <span className="note"> ({flows_correlation.note})</span>
                        </span>
                      )
                    ) : flows_correlation.corr_vix_fii_minus_dii === null ? (
                      <span className="note">{flows_correlation.note}</span>
                    ) : (
                      <span>
                        VIX↑ vs (FII−DII):{" "}
                        <b className={flows_correlation.corr_vix_fii_minus_dii >= 0 ? "pos" : "neg"}>
                          {fmtCorr(flows_correlation.corr_vix_fii_minus_dii)}
                        </b>
                        <span className="note"> ({flows_correlation.note})</span>
                      </span>
                    )}
                  </div>
                )}

                {flowsTab === "relative" && (
                  <div className="flows-series-note">
                    Z-scores <FieldHelp term="flows_zscore" /> — unitless, standard deviations from this window's
                    own mean, not ₹Cr.
                  </div>
                )}
                {flowsTab === "cumulative" && (
                  <div className="flows-series-note">
                    <FieldHelp term="flows_cumulative" /> Read the slope, not the absolute level.
                  </div>
                )}
                {flowsTab === "net" && (
                  <div className="flows-series-note">
                    <FieldHelp term="flows_net_tug_of_war" /> Positive = FII buying more (or selling less) than
                    DII that day.
                  </div>
                )}
                {flowsTab === "rolling" && (
                  <div className="flows-series-note">
                    <FieldHelp term="flows_rolling_correlation" /> correlation ≠ causation, and a break in a line
                    means too few real days in that trailing window.
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="cols2">
        <div className="detail-card">
          <div className="detail-card-head">
            <span>
              Sectors today <span className="note">real day-change % across all 17 indices</span>
            </span>
            {allIndices && allIndices.length > 0 && (
              <AsOfBadge
                date={allIndices.reduce<string | null>(
                  (oldest, i) => (i.bar_date && (!oldest || i.bar_date < oldest) ? i.bar_date : oldest),
                  null,
                )}
                reference={data.expected_as_of}
              />
            )}
          </div>
          <div className="context-scroll">
            {!allIndices ? (
              <div className="empty-state" style={{ padding: 20 }}>
                Loading…
              </div>
            ) : (
              <SectorHeat indices={allIndices} />
            )}
          </div>
        </div>

        <div className="detail-card">
          <div className="detail-card-head">
            <span>Macro &amp; events</span>
          </div>
          <div className="context-scroll">
            <div className="prov-section-label">Upcoming</div>
            {macro_upcoming.length === 0 ? (
              <div className="mut" style={{ fontSize: "0.82rem" }}>
                None captured.
              </div>
            ) : (
              macro_upcoming.map((e, i) => (
                <div className="ev" key={i}>
                  <span className="ev-d">{e.event_date}</span>
                  <CountryBadge country={e.country} />
                  <span className="ev-b">{e.event_type}</span>
                  <span className="ev-t">{e.description}</span>
                </div>
              ))
            )}
            <div className="prov-section-label" style={{ marginTop: 8 }}>Recent</div>
            {macro_recent.length === 0 ? (
              <div className="mut" style={{ fontSize: "0.82rem" }}>
                None captured.
              </div>
            ) : (
              macro_recent.map((e, i) => (
                <div className="ev" key={i}>
                  <span className="ev-d">{e.event_date}</span>
                  <CountryBadge country={e.country} />
                  <span className="ev-b mut">{e.event_type}</span>
                  <span className="ev-t">{e.description}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="detail-card valuation-strip">
        <span className="valuation-strip-label">
          NIFTY valuation band <FieldHelp term="nifty_valuation_band" />
        </span>
        {!valuation.available ? (
          <span className="note">{valuation.note}</span>
        ) : (
          <>
            <span className="valuation-strip-values">
              <span>P/E <b>{valuation.pe?.toFixed(2)}×</b></span>
              <span>P/B <b>{valuation.pb?.toFixed(2)}×</b></span>
              <span>Div yield <b>{valuation.div_yield?.toFixed(2)}%</b></span>
              {valuation.classification && (
                <span className={`valuation-chip ${valuation.classification}`}>
                  {valuation.classification}
                </span>
              )}
              {valuation.mean_pe !== null && valuation.sd_pe !== null && (
                <span className="mut" style={{ fontSize: "0.76rem" }}>
                  vs {valuation.mean_pe.toFixed(1)}× ± {valuation.sd_pe.toFixed(1)} (n={valuation.band_n})
                </span>
              )}
              <AsOfBadge date={valuation.as_of_date} reference={data.expected_as_of} />
            </span>
            {valuation.methodology_note && (
              <div className="valuation-methodology-note">{valuation.methodology_note}</div>
            )}
            {valuation.pe_series.length > 0 && (
              <div style={{ flex: "1 0 100%", marginTop: 10 }}>
                <ValuationBandChart
                  series={valuation.pe_series}
                  meanPe={valuation.mean_pe} sdPe={valuation.sd_pe}
                  currentPe={valuation.pe} currentDate={valuation.as_of_date}
                  classification={valuation.classification}
                />
              </div>
            )}
          </>
        )}
      </div>

      {cape.available && (cape.pre?.available || cape.post?.available) && (
        <div className="detail-card valuation-strip">
          <span className="valuation-strip-label">
            NIFTY CAPE — fair-value range <FieldHelp term="cape_fair_value" />
          </span>
          {cape.nominal_note && <div className="valuation-methodology-note">{cape.nominal_note}</div>}
          {cape.segmentation_note && <div className="valuation-methodology-note">{cape.segmentation_note}</div>}
          <div style={{ flex: "1 0 100%", marginTop: 10 }}>
            <CapeChart pre={cape.pre} post={cape.post} />
          </div>
        </div>
      )}

      {/* Repositioned to the bottom of the page (2026-11-01) -- below all
          market content, so breadth/flows/benchmarks/valuation load and
          render first. Panel itself unchanged. */}
      <MarketNewsPanel token={token} />
    </>
  );
}
